import { BulkOrderStatusEnum } from "@/requests/gen/react-query/fastAPI.schemas";
import { ColumnDef } from "@tanstack/react-table";
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  ParamDataTableToolbar,
  ParamSearchFilter,
  ParamDateFilter,
  ParamClearButton,
  ParamDataTable,
  dataTableSearchParsers,
} from "@/components/ui/param-data-table";
import {
  useGetBulkOrders,
  getGetBulkOrdersQueryOptions,
} from "@/requests/gen/react-query/bulk-order";
import formatUSD from "@/lib/format-currency";
import { format, parseISO } from "date-fns";
import {
  createFileRoute,
  Link,
  linkOptions,
  type LinkProps,
} from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import {
  useQueryStates,
  createLoader,
  parseAsArrayOf,
  parseAsStringEnum,
  type inferParserType,
} from "nuqs";
import { keepPreviousData } from "@tanstack/react-query";
import { Header } from "@/components/universal/header";

const bulkReorderSearchParsers = {
  statuses: parseAsArrayOf(
    parseAsStringEnum(Object.values(BulkOrderStatusEnum)),
  ),
};

const bulkReorderSearchAllParsers = {
  ...dataTableSearchParsers,
  ...bulkReorderSearchParsers,
};

const loadBulkReorderSearch = createLoader(bulkReorderSearchAllParsers);

type BulkReorderSearch = inferParserType<typeof bulkReorderSearchAllParsers>;

function bulkReorderQueryParams(
  p: BulkReorderSearch,
): Parameters<typeof getGetBulkOrdersQueryOptions>[0] {
  return {
    page: p.page,
    limit: p.limit,
    search: p.search,
    sort_column: p.sort_column,
    ascending: p.ascending,
    bulk_order_statuses: p.statuses,
    created_at_start: p.created_at_start,
    created_at_end: p.created_at_end,
    is_reorder: true,
  };
}

export const Route = createFileRoute(
  "/_private/_practice-360/bulk-reorder/lookup",
)({
  loader: ({ context: { queryClient }, location }) =>
    queryClient.prefetchQuery(
      getGetBulkOrdersQueryOptions(
        bulkReorderQueryParams(loadBulkReorderSearch(location.searchStr)),
      ),
    ),
  component: BulkReorderLookupPage,
});

// Define the columns for the table
interface BulkReorderTableProps {
  link?: LinkProps;
  bulk_order_id: string;
  created_at: string;
  total: string;
  next_run_at?: string;
  entity: string;
}

// Define the columns headers and accessor keys
const columns: ColumnDef<BulkReorderTableProps>[] = [
  { header: "Bulk Reorder ID", accessorKey: "bulk_order_id" },
  {
    header: "Created At",
    accessorKey: "created_at",
    cell: (value) => {
      const dateString = value.getValue() as string;
      if (!dateString) {
        return "";
      }
      return format(parseISO(dateString), "MM-dd-yyyy");
    },

    enableSorting: true,
  },
  { header: "Total", accessorKey: "total" },
  {
    header: "Next Run At",
    accessorKey: "next_run_at",
    cell: (value) => {
      const dateString = value.getValue() as string;
      if (!dateString) {
        return "";
      }
      return format(parseISO(dateString), "MM-dd-yyyy");
    },
  },
  { header: "Entity", accessorKey: "entity" },
];

// Main component
function BulkReorderLookupPage() {
  const [queryParams] = useQueryStates(bulkReorderSearchAllParsers);

  // Get the bulk_orders
  const bulk_orders = useGetBulkOrders(bulkReorderQueryParams(queryParams), {
    query: {
      placeholderData: keepPreviousData,
    },
  });

  // Map the data to the table format
  const tableData: BulkReorderTableProps[] | undefined =
    bulk_orders.data?.items.map((bulk_order) => {
      return {
        link: linkOptions({
          to: "/bulk-order/$bulkOrderId",
          params: { bulkOrderId: String(bulk_order.bulk_order_id) },
        }),
        bulk_order_id: bulk_order.bulk_order_id,
        created_at: bulk_order.created_at,
        total: formatUSD(bulk_order.total_price),
        next_run_at: bulk_order?.bulk_reorder_schedule?.next_run_at,
        entity: bulk_order.entity.name,
      };
    });

  return (
    <div className="text-left">
      <title>Bulk Reorders - Legrande Health Portal</title>
      <Header
        crumbs={[
          { name: "Bulk Reorders", link: { to: "/bulk-reorder/lookup" } },
        ]}
      />
      <Card>
        <CardHeader>
          <CardTitle>Automatic Bulk Reorders</CardTitle>
          <CardDescription>
            View your automatic bulk reorders below. You can search, filter, and
            click to see more details.
          </CardDescription>
          <CardAction>
            <Button variant="default" size="sm" asChild>
              <Link to="/bulk-order/create" search={{ is_reorder: true }}>
                <Plus />
                Create Auto Reorder
              </Link>
            </Button>
          </CardAction>
        </CardHeader>
        <CardContent>
          <ParamDataTableToolbar>
            <ParamSearchFilter isFetching={bulk_orders.isFetching} />
            <ParamDateFilter title="Created At" name="created_at" />
            <ParamClearButton />
          </ParamDataTableToolbar>
          <ParamDataTable
            columns={columns}
            data={tableData ?? []}
            isLoading={bulk_orders.isLoading}
            isError={bulk_orders.isError}
            count={bulk_orders.data?.count}
          />
        </CardContent>
      </Card>
    </div>
  );
}
